import { useEffect, useMemo } from 'react';
import { ABS_SORT_KEYS, getLibraryItems, getMe, getPersonalized } from '../api/abs';
import { connectionsStore } from '../api/connections';
import { AbsError } from '../api/client';
import { FINISHED_THRESHOLD, isAudiobookItem, isSeriesEntity, mediaTypeOf, progressItemIds } from '../api/helpers';
import { finishedEntries, getBooksInProgress, getFinishedBooks, hydrateProgressEntries, inProgressEntries, mediaProgressOf } from '../api/progressLists';
import { confirmProgressMutation, progressStore, setProgressFromUser, setProgressMutation, type ProgressMutation } from '../api/progressStore';
import { getSession, sessionStore } from '../api/session';
import type { LibraryItem, MediaProgress, Series, Shelf } from '../api/types';
import { downloadsStore, getDownloadRecord } from '../downloads/manager';
import { fetchQuery, invalidate } from '../lib/query';
import { createStore, useStore } from '../lib/store';
import { activeLibraryStore, resolveActiveLibraryId } from './activeLibrary';
import { prefetchHomeSections } from './groups';
import { normalizeHomeSectionId } from './homePrefs';
import { findCachedBook, getCachedBooks, loadScope } from './library/cache';
import { hasActiveFilters, normalizeFilterOptions, type BookFilterOptions } from './library/filters';
import { pageBooks, queryLibrary } from './library/query';
import { getLibrarySyncState, syncLibrary } from './library/sync';
import {
  bookFromDownload,
  bookFromLibraryItem,
  downloadedIds,
  getDownloadedIds,
  isBookEffectivelyFinished,
  seriesFromAbs,
  withProgress,
  type BookSummary,
  type HomeShelf,
  type ShelfItem,
} from './models';
import { libraryScopeKey, userScopeKey } from './scope';

// Home data: port of Flutter's HomeProvider (lib/providers/home_provider.dart,
// spec 02 §2). Shelves come from ABS personalized shelves, then Continue
// Listening and Finished are replaced from the user's progress; a Downloaded
// shelf is added from local downloads. 5-minute cache with a background
// refresh after 1 minute; request-version + scope guards drop stale results.

export const HOME_CACHE_TTL_MS = 5 * 60_000;
export const HOME_SWR_THRESHOLD_MS = 60_000;
const HYDRATE_CONCURRENCY = 4;
/** Books in Recently Added (the size of an ABS personalized shelf). */
const RECENTLY_ADDED_LIMIT = 10;

export type HomeProgressMutation = ProgressMutation;

export interface HomeState {
  shelves: HomeShelf[];
  /** Starts true, like Flutter (Home opens in its loading state). */
  isLoading: boolean;
  /** A foreground fetch is running (first load, pull-to-refresh); the silent background refresh never sets it. */
  isRefreshing: boolean;
  /** User-facing message of the last failed load. */
  error: string | null;
  errorDetail: unknown;
  /** The server was unreachable and only downloads are shown. */
  offline: boolean;
  /** Epoch ms of the last successful commit. */
  lastFetchAt: number | null;
  /** A connection switch is verifying its target. */
  isSwitching: boolean;
  /** Library the shelves were loaded for. */
  libraryId: string | null;
  /** `userScope|libraryId` the shelves belong to. */
  fingerprint: string | null;
}

const INITIAL: HomeState = {
  shelves: [],
  isLoading: true,
  isRefreshing: false,
  error: null,
  errorDetail: null,
  offline: false,
  lastFetchAt: null,
  isSwitching: false,
  libraryId: null,
  fingerprint: null,
};

export const homeStore = createStore<HomeState>(INITIAL);

/** Flutter `isTransitioning`: switching server, or reloading while shelves are shown (drives the overlay). */
export function isHomeTransitioning(state: HomeState): boolean {
  return state.isSwitching || (state.isLoading && state.shelves.length > 0);
}

// ---------------------------------------------------------------- shelf normalisation (pure)

const norm = (s: string | undefined): string => (s ?? '').trim().toLowerCase();

/** Flutter `_shelfRank`: Continue Listening 10, Recently Added 20, others 50, Finished 80, Downloaded 1000. */
export function shelfRank(shelf: Pick<HomeShelf, 'id' | 'label'>): number {
  const id = norm(shelf.id);
  const label = norm(shelf.label);
  if (id === 'continue-listening' || label === 'continue listening') return 10;
  if (id === 'manual-recent' || id.includes('recently')) return 20;
  if (label.includes('recently added')) return 20;
  if (id === 'finished' || label === 'finished') return 80;
  if (id === 'downloaded' || label === 'downloaded') return 1000;
  return 50;
}

/** Stable sort by rank, ties keep their original order. */
export function sortShelvesForStableHome(shelves: readonly HomeShelf[]): HomeShelf[] {
  return shelves
    .map((shelf, index) => ({ shelf, index, rank: shelfRank(shelf) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((x) => x.shelf);
}

/** When more than one shelf has `label`, merge their items by id (later wins) into one shelf appended at the end. */
export function mergeSpecialShelves(shelves: readonly HomeShelf[], label: string, mergedId: string): HomeShelf[] {
  const target = norm(label);
  const matches = shelves.filter((s) => norm(s.label) === target);
  if (matches.length <= 1) return [...shelves];
  const byId = new Map<string, ShelfItem>();
  for (const shelf of matches) for (const item of shelf.items) byId.set(item.id, item);
  return [...shelves.filter((s) => norm(s.label) !== target), { id: mergedId, label, items: [...byId.values()] }];
}

/** Replaces (or adds, or removes when empty) the shelf with `label` / `preferredId`, keeping an existing id. */
export function upsertSpecialShelf(shelves: readonly HomeShelf[], label: string, preferredId: string, items: ShelfItem[]): HomeShelf[] {
  const matchesTarget = (s: HomeShelf) => norm(s.label) === norm(label) || norm(s.id) === norm(preferredId);
  if (items.length === 0) return sortShelvesForStableHome(shelves.filter((s) => !matchesTarget(s)));
  const index = shelves.findIndex(matchesTarget);
  const next = [...shelves];
  if (index < 0) next.push({ id: preferredId, label, items });
  else next[index] = { id: next[index]?.id || preferredId, label, items };
  return sortShelvesForStableHome(next);
}

/**
 * Recently Added shows the library's `RECENTLY_ADDED_LIMIT` newest books,
 * replacing ABS' shelf (or adding it when ABS left it out: nothing added in
 * the last 60 days). Null or empty keeps ABS' shelf.
 */
export function withNewestRecentlyAdded(shelves: readonly HomeShelf[], newest: readonly BookSummary[] | null): HomeShelf[] {
  if (!newest || newest.length === 0) return [...shelves];
  return upsertSpecialShelf(shelves, 'Recently Added', 'recently-added', newest.slice(0, RECENTLY_ADDED_LIMIT));
}

/** `_normalizeShelves`: "Listen Again" → "Finished", merge duplicate special shelves, rank sort. */
export function normalizeShelves(shelves: readonly HomeShelf[]): HomeShelf[] {
  let next = shelves.map((s) => (s.label === 'Listen Again' ? { ...s, label: 'Finished' } : s));
  next = mergeSpecialShelves(next, 'Finished', 'finished');
  next = mergeSpecialShelves(next, 'Continue Listening', 'continue-listening');
  return sortShelvesForStableHome(next);
}

export interface ShelfParseContext {
  progress?: Record<string, MediaProgress>;
  downloaded?: Set<string>;
}

/**
 * ABS personalized shelves → book/series shelves (Flutter E11): shelves whose
 * id mentions series or authors are dropped (Home builds those rows itself),
 * podcasts are dropped, and empty shelves are skipped.
 */
export function parsePersonalizedShelves(data: unknown, ctx: ShelfParseContext = {}): HomeShelf[] {
  if (!Array.isArray(data)) return [];
  const out: HomeShelf[] = [];
  for (const raw of data as Array<Partial<Shelf>>) {
    if (!raw || typeof raw !== 'object') continue;
    const id = typeof raw.id === 'string' ? raw.id : null;
    const label = raw.label ?? raw.title ?? raw.category ?? 'Unknown';
    const entities = Array.isArray(raw.entities) ? raw.entities : null;
    if (!id || !entities) continue;
    const lowerId = id.toLowerCase();
    if (lowerId.includes('series') || lowerId.includes('author')) continue;
    const items: ShelfItem[] = [];
    for (const entity of entities) {
      if (!entity || typeof entity !== 'object' || !isAudiobookItem(entity as LibraryItem)) continue;
      try {
        if (mediaTypeOf(entity as LibraryItem) === 'series' || isSeriesEntity(entity)) items.push(seriesFromAbs(entity as Series));
        else {
          const item = entity as LibraryItem;
          items.push(bookFromLibraryItem(item, { progress: ctx.progress?.[item.id], isDownloaded: ctx.downloaded?.has(item.id) ?? false }));
        }
      } catch {
        // One malformed entity never drops the shelf.
      }
    }
    if (items.length > 0) out.push({ id, label, items });
  }
  return out;
}

/**
 * Ids of the Continue Listening books that are finished (the flag, `finishedAt`
 * or ≥ 98 %) once live progress is joined in: a book ABS's own shelf still
 * lists, one that ended while playing, or one marked finished elsewhere.
 */
export function finishedContinueListeningIds(shelves: readonly HomeShelf[], progress: Readonly<Record<string, MediaProgress>>): string[] {
  const ids: string[] = [];
  for (const shelf of shelves) {
    if (!isContinueShelf(shelf)) continue;
    for (const item of shelf.items) {
      if (item.kind === 'book' && isBookEffectivelyFinished(withProgress(item, progress[item.id]))) ids.push(item.id);
    }
  }
  return ids;
}

/** Drops `finishedIds` from Continue Listening (and the shelf when that empties it); the same array when nothing is dropped. */
export function withoutFinishedContinueListening(shelves: readonly HomeShelf[], finishedIds: ReadonlySet<string>): HomeShelf[] {
  if (finishedIds.size === 0) return shelves as HomeShelf[];
  const next: HomeShelf[] = [];
  for (const shelf of shelves) {
    if (!isContinueShelf(shelf)) {
      next.push(shelf);
      continue;
    }
    const items = shelf.items.filter((item) => item.kind !== 'book' || !finishedIds.has(item.id));
    if (items.length > 0) next.push(items.length === shelf.items.length ? shelf : { ...shelf, items });
  }
  return next;
}

/** The Finished shelf lists the 10 most recently finished books (`enrichFromProgress`). */
export const FINISHED_SHELF_LIMIT = 10;

const isFinishedShelfOf = (shelf: Pick<HomeShelf, 'id' | 'label'>) => norm(shelf.id) === 'finished' || norm(shelf.label) === 'finished';

/**
 * Optimistic shelf update after a quick action (Flutter `applyProgressMutation`). Null when nothing changed.
 * A book marked finished also moves to the front of the Finished shelf (added there, or the shelf created,
 * when it was not on it; `book` supplies it when no other shelf holds it), which keeps its 10 newest.
 */
export function applyProgressMutationToShelves(
  shelves: readonly HomeShelf[],
  bookId: string,
  mutation: HomeProgressMutation,
  now = Date.now(),
  book?: BookSummary,
): HomeShelf[] | null {
  let changed = false;
  let finishedCopy: BookSummary | null = null;
  const next: HomeShelf[] = [];
  for (const shelf of shelves) {
    const isFinishedShelf = isFinishedShelfOf(shelf);
    const isContinue = norm(shelf.id) === 'continue-listening' || norm(shelf.label) === 'continue listening';
    const items: ShelfItem[] = [];
    for (const item of shelf.items) {
      if (item.kind !== 'book' || item.id !== bookId) {
        items.push(item);
        continue;
      }
      changed = true;
      // `lastUpdate: now` makes the copy win over older progress joined in at render time.
      if (mutation === 'progressReset') {
        if (isFinishedShelf || isContinue) continue;
        items.push({ ...item, currentTime: 0, progress: 0, isFinished: false, finishedAt: null, lastUpdate: now });
      } else if (mutation === 'markedFinished') {
        // The position is kept (Flutter writes the duration), so "Mark as Unfinished" returns to it.
        const copy: BookSummary = { ...item, progress: 1, isFinished: true, finishedAt: now, lastUpdate: now };
        finishedCopy ??= copy;
        // Continue Listening drops it; the Finished shelf puts it first (below).
        if (isContinue || isFinishedShelf) continue;
        items.push(copy);
      } else {
        if (isFinishedShelf) continue;
        const ratio = item.duration > 0 ? Math.min(1, item.currentTime / item.duration) : 0;
        // A position in the finished zone would still read as finished (progressAfterMutation).
        const atEnd = ratio >= FINISHED_THRESHOLD;
        items.push({ ...item, isFinished: false, finishedAt: null, currentTime: atEnd ? 0 : item.currentTime, progress: atEnd ? 0 : ratio, lastUpdate: now });
      }
    }
    if (items.length === 0 && (isContinue || (isFinishedShelf && mutation !== 'markedFinished'))) continue;
    next.push(items.length === shelf.items.length && items.every((x, i) => x === shelf.items[i]) ? shelf : { ...shelf, items });
  }
  if (mutation === 'markedFinished') {
    finishedCopy ??= book && book.id === bookId ? { ...book, progress: 1, isFinished: true, finishedAt: now, lastUpdate: now } : null;
    if (finishedCopy) {
      changed = true;
      const index = next.findIndex(isFinishedShelfOf);
      const finishedShelf = index >= 0 ? next[index] : undefined;
      const items = [finishedCopy, ...(finishedShelf?.items ?? [])].slice(0, FINISHED_SHELF_LIMIT);
      if (finishedShelf) next[index] = { ...finishedShelf, items };
      else next.push({ id: 'finished', label: 'Finished', items });
    }
  }
  return changed ? sortShelvesForStableHome(next) : null;
}

// ---------------------------------------------------------------- section composition (pure)

/** One row of the Home list, in display order. */
export type HomeSection =
  | { kind: 'carousel'; shelf: HomeShelf; prefId: string }
  | { kind: 'shelf'; shelf: HomeShelf; prefId: string; compact: boolean }
  | { kind: 'recentSeries' }
  | { kind: 'newestAuthors' };

export const RECENT_SERIES_SECTION_ID = 'recent-series';
export const NEWEST_AUTHORS_SECTION_ID = 'recent-authors';

/** Preference id of a shelf: normalised id, else label. */
export function shelfPreferenceId(shelf: Pick<HomeShelf, 'id' | 'label'>): string {
  return normalizeHomeSectionId(shelf.id || shelf.label);
}

const isContinueShelf = (shelf: HomeShelf) => norm(shelf.id) === 'continue-listening' || norm(shelf.label) === 'continue listening';
const isRecentlyAddedShelf = (shelf: HomeShelf) => shelf.id.includes('recently') || norm(shelf.label).includes('recently added');

/**
 * Home list order (Flutter `_buildListItemBuilders`, spec 02 §4.5): visible
 * shelves in rank order, Continue Listening as a carousel unless compact,
 * Recent Series right after Recently Added (else after the last shelf),
 * Newest Authors last.
 */
export function composeHomeSections(
  shelves: readonly HomeShelf[],
  prefs: { disabled: ReadonlySet<string>; compact: ReadonlySet<string> },
): { sections: HomeSection[]; visibleShelves: HomeShelf[]; hasOnlyHiddenSections: boolean } {
  const visibleShelves = shelves.filter((s) => !prefs.disabled.has(shelfPreferenceId(s)));
  const showRecentSeries = !prefs.disabled.has(RECENT_SERIES_SECTION_ID);
  const showNewestAuthors = !prefs.disabled.has(NEWEST_AUTHORS_SECTION_ID);
  const sections: HomeSection[] = [];
  let recentAddedFound = false;
  for (const shelf of visibleShelves) {
    const prefId = shelfPreferenceId(shelf);
    const continueShelf = isContinueShelf(shelf);
    const compact = continueShelf && prefs.compact.has('continue-listening');
    sections.push(continueShelf && !compact ? { kind: 'carousel', shelf, prefId } : { kind: 'shelf', shelf, prefId, compact });
    if (isRecentlyAddedShelf(shelf)) {
      recentAddedFound = true;
      if (showRecentSeries) sections.push({ kind: 'recentSeries' });
    }
  }
  if (!recentAddedFound && showRecentSeries) sections.push({ kind: 'recentSeries' });
  if (showNewestAuthors) sections.push({ kind: 'newestAuthors' });
  return { sections, visibleShelves, hasOnlyHiddenSections: shelves.length > 0 && visibleShelves.length === 0 };
}

// ---------------------------------------------------------------- provider

let activeFetch: Promise<void> | null = null;
let requestVersion = 0;
let consumers = 0;

function patch(next: Partial<HomeState>): void {
  homeStore.set((s) => ({ ...s, ...next }));
}

function currentFingerprint(): string | null {
  const session = getSession();
  if (!session) return null;
  const lib = activeLibraryStore.get();
  return `${userScopeKey(session)}|${lib.libraryId ?? lib.requestedLibraryId}`;
}

/** Scope changes reset Home (Flutter `updateDependencies`). */
function onDependencies(): void {
  const fingerprint = currentFingerprint();
  const switching = connectionsStore.get().switching;
  const state = homeStore.get();
  if (fingerprint === state.fingerprint) {
    if (state.isSwitching !== switching) patch({ isSwitching: switching });
    return;
  }
  requestVersion++;
  activeFetch = null;
  const sameAccount = !!fingerprint && !!state.fingerprint && fingerprint.split('|').slice(0, 3).join('|') === state.fingerprint.split('|').slice(0, 3).join('|');
  homeStore.set({
    ...INITIAL,
    // A library change on the same account keeps the old shelves under the overlay;
    // another account's shelves would point at the wrong server, so they go.
    shelves: sameAccount ? state.shelves : [],
    isSwitching: switching,
    fingerprint,
  });
  if (fingerprint && consumers > 0) void loadHome(true);
}

sessionStore.subscribe(onDependencies);
activeLibraryStore.subscribe(onDependencies);
connectionsStore.subscribe(onDependencies);

// Back online after a failed load (offline shelf or error view): load again, as a pull-to-refresh would.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    const state = homeStore.get();
    if (consumers > 0 && getSession() && (state.offline || state.error != null)) void loadHome(true);
  });
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong. Please try again.';
}

function downloadedShelfBooks(): BookSummary[] {
  const ids = downloadedIds(downloadsStore.get());
  const progress = progressStore.get();
  return Object.values(downloadsStore.get())
    .filter((d) => ids.has(d.itemId))
    .sort((a, b) => a.createdAt - b.createdAt)
    .map((d) => bookFromDownload(getDownloadRecord(d.itemId) ?? d, progress[d.itemId]));
}

function withDownloadedShelf(shelves: HomeShelf[]): HomeShelf[] {
  const downloads = downloadedShelfBooks();
  return downloads.length > 0 ? [...shelves, { id: 'downloaded', label: 'Downloaded', items: downloads }] : shelves;
}

function toBooks(items: LibraryItem[]): BookSummary[] {
  const downloaded = downloadedIds(downloadsStore.get());
  return items.map((i) => bookFromLibraryItem(i, { isDownloaded: downloaded.has(i.id) }));
}

/**
 * Loads Home (Flutter `fetchShelves`): joins a fetch in flight; within 5
 * minutes returns cached shelves, refreshing silently after 1 minute;
 * `force` always refetches.
 */
export async function loadHome(force = false): Promise<void> {
  onDependencies();
  if (!getSession()) return;
  if (activeFetch) {
    await activeFetch;
    return;
  }
  const state = homeStore.get();
  const age = state.lastFetchAt != null ? Date.now() - state.lastFetchAt : Infinity;
  if (!force && state.shelves.length > 0 && age < HOME_CACHE_TTL_MS) {
    if (state.error) patch({ error: null, errorDetail: null });
    if (age >= HOME_SWR_THRESHOLD_MS) {
      const background = refreshShelves(false);
      activeFetch = background;
      void background.finally(() => {
        if (activeFetch === background) activeFetch = null;
      });
    }
    return;
  }
  const run = refreshShelves(state.shelves.length === 0 || force);
  activeFetch = run;
  try {
    await run;
  } finally {
    if (activeFetch === run) activeFetch = null;
  }
}

/** Pull-to-refresh. */
export function refreshHome(): Promise<void> {
  return loadHome(true);
}

async function refreshShelves(showLoading: boolean): Promise<void> {
  const requestId = ++requestVersion;
  const fingerprint = homeStore.get().fingerprint;
  const isCurrent = () => requestId === requestVersion && fingerprint === homeStore.get().fingerprint;
  // Flutter's background refresh only clears errors (no spinner, no notify).
  patch(showLoading ? { isLoading: true, isRefreshing: true, error: null, errorDetail: null } : { error: null, errorDetail: null });
  try {
    if (activeLibraryStore.get().noEnabledLibraries) {
      // Every library is disabled for this connection: nothing to fetch, Home shows "No libraries enabled".
      commit([], null);
      return;
    }
    const libraryId = await resolveActiveLibraryId();
    if (!isCurrent()) return;
    if (!libraryId) {
      await loadManualFallback(isCurrent);
      return;
    }
    const [raw, newest] = await Promise.all([getPersonalized(libraryId), newestBooks(libraryId)]);
    if (!isCurrent()) return;
    const shelves = parsePersonalizedShelves(raw, { progress: progressStore.get(), downloaded: downloadedIds(downloadsStore.get()) });
    commit(withDownloadedShelf(withNewestRecentlyAdded(normalizeShelves(shelves), newest)), libraryId);
    prefetchHomeSections(libraryId);
    await enrichFromProgress(isCurrent);
  } catch (err) {
    if (!isCurrent()) return;
    if (err instanceof AbsError && err.isNetworkError && !err.isTimeout && homeStore.get().shelves.length === 0) {
      // Unreachable with nothing cached: show the downloads on this device, under the error banner
      // ("No connection", Retry). Without downloads the error view shows instead of an empty Home.
      const downloaded = withDownloadedShelf([]);
      if (downloaded.length > 0) {
        patch({ shelves: sortShelvesForStableHome(downloaded), isLoading: false, offline: true, error: errorMessage(err), errorDetail: err });
        return;
      }
    }
    patch({ isLoading: false, offline: false, error: errorMessage(err), errorDetail: err });
  } finally {
    if (isCurrent()) patch({ isRefreshing: false });
  }
}

/**
 * The library's newest books, whenever they were added (ABS' `recently-added`
 * shelf only lists the last 60 days). Null when they could not be loaded.
 */
async function newestBooks(libraryId: string): Promise<BookSummary[] | null> {
  try {
    const page = await getLibraryItems(libraryId, { limit: RECENTLY_ADDED_LIMIT, page: 0, sort: ABS_SORT_KEYS.dateAdded, desc: true, include: 'progress' });
    return toBooks((page.results ?? []).filter((i) => isAudiobookItem(i)));
  } catch {
    return null;
  }
}

function commit(shelves: HomeShelf[], libraryId: string | null): void {
  patch({ shelves: sortShelvesForStableHome(shelves), isLoading: false, error: null, errorDetail: null, offline: false, lastFetchAt: Date.now(), libraryId });
}

/**
 * No library resolvable (Flutter branch C): recent additions across the
 * enabled audiobook libraries + Finished. Fails when every request failed.
 */
async function loadManualFallback(isCurrent: () => boolean): Promise<void> {
  const libraries = activeLibraryStore.get().bookLibraries;
  const [recentPages, finishedResult] = await Promise.all([
    Promise.allSettled(libraries.map((l) => getLibraryItems(l.id, { limit: 50, page: 0, sort: ABS_SORT_KEYS.dateAdded, desc: true, include: 'progress' }))),
    getFinishedBooks({ limit: FINISHED_SHELF_LIMIT, concurrency: HYDRATE_CONCURRENCY }).then(
      (items) => ({ status: 'fulfilled' as const, value: items }),
      (reason: unknown) => ({ status: 'rejected' as const, reason }),
    ),
  ]);
  if (!isCurrent()) return;
  const results = [...recentPages, finishedResult];
  const firstFailure = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (firstFailure && results.every((r) => r.status === 'rejected')) throw firstFailure.reason;
  const finished = finishedResult.status === 'fulfilled' ? finishedResult.value : [];
  const recent = recentPages
    .flatMap((r) => (r.status === 'fulfilled' ? (r.value.results ?? []) : []))
    .filter((i) => isAudiobookItem(i))
    .sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0))
    .slice(0, 10);
  const shelves: HomeShelf[] = [];
  if (recent.length > 0) shelves.push({ id: 'manual-recent', label: 'Recently Added', items: toBooks(recent) });
  if (finished.length > 0) shelves.push({ id: 'finished', label: 'Finished', items: toBooks(finished) });
  commit(shelves, null);
}

/**
 * Replaces Continue Listening (all in-progress, newest first) and Finished
 * (10 newest) from `/api/me`. A list that could not be loaded completely
 * keeps the shelf already on screen (a partial list would drop books).
 */
async function enrichFromProgress(isCurrent: () => boolean): Promise<void> {
  let progress: ReturnType<typeof mediaProgressOf>;
  try {
    const user = await getMe();
    if (!isCurrent()) return;
    setProgressFromUser(user);
    progress = mediaProgressOf(user);
  } catch {
    return;
  }
  const upsert = (label: string, id: string, items: LibraryItem[]) => {
    if (!isCurrent()) return;
    const state = homeStore.get();
    patch({ shelves: upsertSpecialShelf(state.shelves, label, id, toBooks(items)), lastFetchAt: Date.now() });
  };
  await Promise.all([
    getBooksInProgress({ progress, concurrency: HYDRATE_CONCURRENCY })
      .then((items) => upsert('Continue Listening', 'continue-listening', items))
      .catch(() => undefined),
    getFinishedBooks({ progress, limit: FINISHED_SHELF_LIMIT, concurrency: HYDRATE_CONCURRENCY })
      .then((items) => upsert('Finished', 'finished', items))
      .catch(() => undefined),
  ]);
}

function shelfBook(bookId: string): BookSummary | undefined {
  for (const shelf of homeStore.get().shelves) {
    const hit = shelf.items.find((i): i is BookSummary => i.kind === 'book' && i.id === bookId);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * The optimistic update after a successful Reset Progress / Mark as Finished /
 * Mark as Unfinished, wherever it was triggered (Flutter
 * `applyProgressMutation` + Book Details' finish toggle): writes the complete
 * progress entry (finished → 100 %, `finishedAt` now; unfinished → no
 * `finishedAt`; reset → 0), updates the Home shelves and drops the cached
 * Finished / progress lists. `book` supplies the duration and position when
 * the store has no entry for the item yet.
 */
export function applyProgressMutation(bookId: string, mutation: HomeProgressMutation, book?: Pick<BookSummary, 'duration' | 'currentTime'>): void {
  const now = Date.now();
  const scope = userScopeKey();
  // The whole book (for the Finished shelf) when the caller gave one, else the one Home or the library cache holds.
  const summary = (isBookSummary(book) ? book : undefined) ?? shelfBook(bookId) ?? (scope ? findCachedBook(bookId, `${scope}|`) : undefined);
  const known = book ?? summary;
  const entry = setProgressMutation(bookId, mutation, { duration: known?.duration, currentTime: known?.currentTime, now });
  applyHomeProgressMutation(bookId, mutation, now, summary);
  // The server has the last word on the position (Audiobookshelf restarts an unfinished book).
  void confirmProgressMutation(bookId, entry);
}

function isBookSummary(value: unknown): value is BookSummary {
  const candidate = value as Partial<BookSummary> | undefined;
  return candidate?.kind === 'book' && typeof candidate.id === 'string' && typeof candidate.title === 'string';
}

/** The Home shelves part of `applyProgressMutation` (use that one; this does not touch the progress store). */
export function applyHomeProgressMutation(bookId: string, mutation: HomeProgressMutation, now = Date.now(), book?: BookSummary): void {
  const next = applyProgressMutationToShelves(homeStore.get().shelves, bookId, mutation, now, book);
  if (next) patch({ shelves: next });
  invalidateFinishedSectionCache();
}

/**
 * Home state; loads (non-forced) on mount and refetches when the account or
 * library changes while mounted.
 */
export function useHome(opts: { autoLoad?: boolean } = {}): HomeState {
  const autoLoad = opts.autoLoad ?? true;
  const state = useStore(homeStore);
  useEffect(() => {
    consumers++;
    if (autoLoad) void loadHome();
    return () => {
      consumers--;
    };
  }, [autoLoad]);
  return state;
}

/**
 * Home shelves as shown: Continue Listening never lists a finished book, judged
 * with live progress. Re-renders only when that set of books changes, not on
 * every playback tick.
 */
export function useVisibleHomeShelves(shelves: readonly HomeShelf[]): HomeShelf[] {
  const finishedKey = useStore(progressStore, (all) => finishedContinueListeningIds(shelves, all).join('\n'));
  return useMemo(() => withoutFinishedContinueListening(shelves, new Set(finishedKey ? finishedKey.split('\n') : [])), [shelves, finishedKey]);
}

// ---------------------------------------------------------------- section "View all" data

export type HomeSectionType = 'continueListening' | 'finished' | 'downloaded' | 'recentlyAdded' | 'general';

/** Shelf → "View all" section type (Flutter `_navigateToSection`). */
export function sectionTypeForShelf(shelf: Pick<HomeShelf, 'id' | 'label'>): HomeSectionType {
  const id = norm(shelf.id);
  const label = norm(shelf.label);
  if (id === 'continue-listening' || label === 'continue listening') return 'continueListening';
  if (id === 'finished' || label === 'finished') return 'finished';
  if (id === 'downloaded' || label === 'downloaded') return 'downloaded';
  if (id.includes('recently') || label.includes('recently added')) return 'recentlyAdded';
  return 'general';
}

export interface SectionPageRequest {
  offset?: number;
  limit?: number;
  query?: string;
  options?: Partial<BookFilterOptions>;
  /** Defaults to the active library. */
  libraryId?: string | null;
  /** Pull-to-refresh: skip the cached Finished pages and progress lists. */
  fresh?: boolean;
}

type ProgressEntry = ReturnType<typeof mediaProgressOf>[number];
type ProgressListKind = 'inProgress' | 'finished';

const FINISHED_CACHE_TTL_MS = 3 * 60_000;
const finishedPageCache = new Map<string, { at: number; promise: Promise<BookSummary[]> }>();

/** Searching / filtering Continue Listening or Finished works on the whole list, hydrated once per minute. */
const PROGRESS_LIST_STALE_MS = 60_000;
const PROGRESS_LIST_PREFIX = 'home:progress-list:';
const progressListKey = (kind: ProgressListKind): string => `${PROGRESS_LIST_PREFIX}${userScopeKey() ?? ''}:${kind}`;

/** Drops the 3-minute Finished section cache and the cached progress lists (after progress changes). */
export function invalidateFinishedSectionCache(): void {
  finishedPageCache.clear();
  invalidate(PROGRESS_LIST_PREFIX);
}

/** The user's in-progress or finished entries, in list order, including changes made on this device. */
async function progressEntries(kind: ProgressListKind): Promise<ProgressEntry[]> {
  setProgressFromUser(await getMe());
  const entries = Object.values(progressStore.get()) as ProgressEntry[];
  return kind === 'finished' ? finishedEntries(entries) : inProgressEntries(entries);
}

/**
 * Books for progress entries, in entry order: rows of this account's local
 * library cache (with the entry joined in) where available, `GET /api/items`
 * for the rest.
 */
async function booksForEntries(entries: readonly ProgressEntry[]): Promise<BookSummary[]> {
  const scope = userScopeKey();
  const activeLibraryId = activeLibraryStore.get().libraryId;
  const libraryScope = activeLibraryId ? libraryScopeKey(activeLibraryId) : null;
  if (libraryScope) await loadScope(libraryScope);
  const cachedFor = (entry: ProgressEntry): BookSummary | undefined => {
    if (!scope) return undefined;
    for (const id of progressItemIds(entry)) {
      const hit = findCachedBook(id, `${scope}|`);
      if (hit) return hit;
    }
    return undefined;
  };
  const items = await hydrateProgressEntries(
    entries.filter((e) => !cachedFor(e)),
    HYDRATE_CONCURRENCY,
  );
  const byId = new Map(items.map((i) => [i.id, i] as const));
  const downloaded = getDownloadedIds();
  const out: BookSummary[] = [];
  for (const entry of entries) {
    const cached = cachedFor(entry);
    if (cached) {
      out.push(withProgress({ ...cached, isDownloaded: downloaded.has(cached.id) }, entry));
      continue;
    }
    const item = progressItemIds(entry)
      .map((id) => byId.get(id))
      .find((x): x is LibraryItem => !!x);
    if (item) out.push(bookFromLibraryItem(item, { isDownloaded: downloaded.has(item.id) }));
  }
  return out;
}

function slice<T>(list: readonly T[], limit: number, page: number): T[] {
  if (limit <= 0) return [...list];
  return list.slice(page * limit, page * limit + limit);
}

function progressPage(kind: ProgressListKind, limit: number, page: number): Promise<BookSummary[]> {
  return progressEntries(kind).then((entries) => booksForEntries(slice(entries, limit, page)));
}

function cachedFinishedPage(limit: number, page: number, fresh: boolean): Promise<BookSummary[]> {
  const key = `${userScopeKey() ?? ''}|${limit}|${page}`;
  const hit = finishedPageCache.get(key);
  if (!fresh && hit && Date.now() - hit.at < FINISHED_CACHE_TTL_MS) return hit.promise;
  const promise = progressPage('finished', limit, page);
  finishedPageCache.set(key, { at: Date.now(), promise });
  promise.catch(() => finishedPageCache.delete(key));
  return promise;
}

function allProgressBooks(kind: ProgressListKind, fresh: boolean): Promise<BookSummary[]> {
  const key = progressListKey(kind);
  if (fresh) invalidate(key);
  return fetchQuery(key, async () => booksForEntries(await progressEntries(kind)), { staleMs: PROGRESS_LIST_STALE_MS });
}

function localQuery(books: BookSummary[], req: SectionPageRequest, limit: number): BookSummary[] {
  const { books: matched } = queryLibrary(books, {
    query: req.query,
    options: req.options,
    progress: progressStore.get(),
    downloaded: downloadedIds(downloadsStore.get()),
  });
  return pageBooks(matched, req.offset ?? 0, limit);
}

/** Downloaded "View all": Flutter's case-insensitive substring search on title, author and narrators, then filters and sort. */
function downloadedSectionPage(req: SectionPageRequest, limit: number): BookSummary[] {
  const query = (req.query ?? '').trim().toLowerCase();
  const books = query ? downloadedShelfBooks().filter((b) => [b.title, b.authorName, b.narratorName].some((f) => f.toLowerCase().includes(query))) : downloadedShelfBooks();
  return localQuery(books, { ...req, query: '' }, limit);
}

/** The active library's cached rows, syncing first when the cache is empty. Throws the sync error. */
async function libraryRows(libraryId: string): Promise<BookSummary[]> {
  const scope = libraryScopeKey(libraryId);
  if (!scope) return [];
  const cached = await getCachedBooks(scope);
  if (cached.length > 0) return cached;
  await syncLibrary(libraryId);
  const synced = await getCachedBooks(scope);
  const state = getLibrarySyncState(scope);
  if (synced.length === 0 && state.phase === 'error' && state.error) throw new Error(state.error);
  return synced;
}

/**
 * One page (20 by default) of a Home section's "View all" list
 * (Flutter RemoteBookRepository.searchBooks). Search text and status /
 * availability filters are applied to every section type (Flutter applied
 * them to Downloaded only): Continue Listening / Finished filter their whole
 * list (hydrated once, cached for a minute), the other sections the local
 * library cache (synced first when empty).
 */
export async function fetchSectionBooks(type: HomeSectionType, req: SectionPageRequest = {}): Promise<BookSummary[]> {
  const limit = req.limit ?? 20;
  const offset = Math.max(0, req.offset ?? 0);
  const page = limit > 0 ? Math.floor(offset / limit) : 0;
  const options = normalizeFilterOptions(req.options);
  const query = (req.query ?? '').trim();
  const narrowing = !!query || options.status !== 'all' || options.availability !== 'showAll';
  const fresh = !!req.fresh && offset === 0;

  if (type === 'downloaded') return downloadedSectionPage(req, limit);

  if (type === 'continueListening' || type === 'finished') {
    const kind: ProgressListKind = type === 'finished' || options.status === 'finished' ? 'finished' : 'inProgress';
    if (narrowing) {
      const all = await allProgressBooks(kind, fresh);
      return localQuery(all, { ...req, options: { ...options, status: type === 'finished' ? 'all' : options.status } }, limit);
    }
    return type === 'finished' ? cachedFinishedPage(limit, page, fresh) : progressPage(kind, limit, page);
  }

  const libraryId = req.libraryId ?? (await resolveActiveLibraryId());
  if (!libraryId) return [];
  // Never answer a search with an unfiltered server page.
  if (narrowing) return localQuery(await libraryRows(libraryId), req, limit);
  const sortForced = type === 'recentlyAdded' && !hasActiveFilters(options);
  const data = await getLibraryItems(libraryId, {
    limit,
    page,
    sort: sortForced ? ABS_SORT_KEYS.dateAdded : ABS_SORT_KEYS[options.sortBy],
    desc: sortForced ? true : options.sortOrder === 'descending',
    include: 'progress',
  });
  return toBooks((data.results ?? []).filter((i) => isAudiobookItem(i)));
}
